import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { Product, ProductCategory, ProductCategoryWithCount } from '../models/product_list_response';
import { PurchaseOrder } from '../models/purchase_order_response';
import { StoreInfo } from '../models/store_info_response';
import {
  fetchPurchaseOrderByIdService,
  fetchPurchaseOrdersService,
  fetchStoreInfoService,
  fetchStoreProductCategoriesService,
  fetchStoreProductInfoService,
  fetchStoreProductListService,
  fetchStoreProductsByCategoryService,
  fetchStoreProductsBySearchService,
} from '../services/store.service';

export const fetchStoreInfo = createAsyncThunk(
  'store/fetchStoreInfo',
  async (payload: { storeSlug: string }, thunkAPI) => {
    const storeInfoResponse = await fetchStoreInfoService({ storeSlug: payload.storeSlug });
    if (storeInfoResponse.isSuccessful) {
      return storeInfoResponse.data;
    } else {
      thunkAPI.rejectWithValue('Unable to fetch data.');
      throw new Error('Unable to fetch data.');
    }
  },
);

export const fetchStoreProductList = createAsyncThunk(
  'store/fetchStoreProductList',
  async (payload: { storeSlug: string }, thunkAPI) => {
    const storeProductListResponse = await fetchStoreProductListService({ storeSlug: payload.storeSlug });
    if (storeProductListResponse.isSuccessful) {
      return storeProductListResponse.data;
    } else {
      thunkAPI.rejectWithValue('Unable to fetch data.');
      throw new Error('Unable to fetch data.');
    }
  },
);

export const fetchStoreProductInfo = createAsyncThunk(
  'store/fetchStoreProductInfo',
  async (payload: { storeSlug: string; productId: string }, thunkAPI) => {
    const storeProductResponse = await fetchStoreProductInfoService({
      storeSlug: payload.storeSlug,
      productId: payload.productId,
    });
    if (storeProductResponse.isSuccessful) {
      return storeProductResponse.data;
    } else {
      thunkAPI.rejectWithValue('Unable to fetch data.');
      throw new Error('Unable to fetch data.');
    }
  },
);

export const fetchStoreProductCategories = createAsyncThunk(
  'store/fetchStoreProductCategories',
  async (payload: { storeSlug: string }, thunkAPI) => {
    const storeCategoriesResponse = await fetchStoreProductCategoriesService({
      storeSlug: payload.storeSlug,
    });
    if (storeCategoriesResponse.isSuccessful) {
      return storeCategoriesResponse.data;
    } else {
      thunkAPI.rejectWithValue('Unable to fetch data.');
      throw new Error('Unable to fetch data.');
    }
  },
);

export const fetchStoreProductsByCategory = createAsyncThunk(
  'store/fetchStoreProductsByCategory',
  async (payload: { storeSlug: string; categoryId: string }, thunkAPI) => {
    const storeCategoriesResponse = await fetchStoreProductsByCategoryService({
      storeSlug: payload.storeSlug,
      categoryId: payload.categoryId,
    });
    if (storeCategoriesResponse.isSuccessful) {
      return storeCategoriesResponse.data;
    } else {
      thunkAPI.rejectWithValue('Unable to fetch data.');
      throw new Error('Unable to fetch data.');
    }
  },
);

export const fetchStoreProductsBySearch = createAsyncThunk(
  'store/fetchStoreProductsBySearch',
  async (payload: { storeSlug: string; searchText: string; categoryId?: string }, thunkAPI) => {
    const storeCategoriesResponse = await fetchStoreProductsBySearchService({
      storeSlug: payload.storeSlug,
      searchText: payload.searchText,
      categoryId: payload.categoryId,
    });
    if (storeCategoriesResponse.isSuccessful) {
      return storeCategoriesResponse.data;
    } else {
      thunkAPI.rejectWithValue('Unable to fetch data.');
      throw new Error('Unable to fetch data.');
    }
  },
);

export const fetchPurchaseOrderById = createAsyncThunk(
  'store/fetchPurchaseOrderById',
  async (payload: { storeSlug: string; purchaseOrderId: string; shopperAccountId: string }, thunkAPI) => {
    const purchaseOrderResponse = await fetchPurchaseOrderByIdService({
      storeSlug: payload.storeSlug,
      shopperAccountId: payload.shopperAccountId,
      purchaseOrderId: payload.purchaseOrderId,
    });
    if (purchaseOrderResponse.isSuccessful) {
      return purchaseOrderResponse.data;
    } else {
      thunkAPI.rejectWithValue('Unable to fetch data.');
      throw new Error('Unable to fetch data.');
    }
  },
);

export const fetchPurchaseOrders = createAsyncThunk(
  'store/fetchPurchaseOrders',
  async (payload: { storeSlug: string; shopperAccountId: string }, thunkAPI) => {
    const purchaseOrderResponse = await fetchPurchaseOrdersService({
      storeSlug: payload.storeSlug,
      shopperAccountId: payload.shopperAccountId,
    });
    if (purchaseOrderResponse.isSuccessful) {
      return purchaseOrderResponse.data;
    } else {
      thunkAPI.rejectWithValue('Unable to fetch data.');
      throw new Error('Unable to fetch data.');
    }
  },
);

const storeSlice = createSlice({
  name: 'store',
  initialState: {
    storeInfo: <StoreInfo>{},
    storeProductList: <Product[]>[],
    storeProduct: <Product>{},
    storeProductCategories: <ProductCategoryWithCount[]>[],
    storeProductListByCategory: <Product[]>[],
    searchStoreProductsResult: <Product[]>[],
    purchaseOrderData: <PurchaseOrder>{},
    purchaseOrders: <PurchaseOrder[]>[],
  },
  reducers: {},
  extraReducers: {
    [fetchStoreInfo.fulfilled.type]: (state, action) => {
      state.storeInfo = action.payload.data;
    },
    [fetchStoreProductList.fulfilled.type]: (state, action) => {
      state.storeProductList = action.payload.data;
    },
    [fetchStoreProductInfo.fulfilled.type]: (state, action) => {
      state.storeProduct = action.payload.data;
    },
    [fetchStoreProductCategories.fulfilled.type]: (state, action) => {
      state.storeProductCategories = action.payload.data;
    },
    [fetchStoreProductsByCategory.fulfilled.type]: (state, action) => {
      state.storeProductListByCategory = action.payload.data;
    },
    [fetchStoreProductsBySearch.fulfilled.type]: (state, action) => {
      state.searchStoreProductsResult = action.payload.data;
    },
    [fetchPurchaseOrderById.fulfilled.type]: (state, action) => {
      state.purchaseOrderData = action.payload.data;
    },
    [fetchPurchaseOrders.fulfilled.type]: (state, action) => {
      state.purchaseOrders = action.payload.data;
    },
  },
});

export default storeSlice.reducer;
